import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, ChevronDown, Target } from 'lucide-react';
import useApi from '../api';
import { formatDate } from '../utils/dateFormatter';
import { StageBadge, useLifecycleStages } from './lifecycleStages';

/**
 * Objectives card for the account detail page: the org's active objective
 * definitions joined with this account's progress. Account-level statuses
 * are editable inline (manual source); person-level rows summarize per-
 * contact progress recorded by the AI or the customer's SDK.
 */

interface ObjectiveInstanceView {
  status: 'in_progress' | 'done';
  status_source: string;
  status_updated_at?: string | null;
  completed_at?: string | null;
  lead_id?: string;
  name?: string;
  email?: string;
}

interface AccountObjectiveItem {
  id: string;
  key: string;
  name: string;
  description: string;
  level: 'account' | 'person';
  stage: string | null;
  gating: string;
  instance?: ObjectiveInstanceView | null;
  people?: ObjectiveInstanceView[];
}

const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  done: 'Done',
  skipped: 'Skipped',
};

const STATUS_TONES: Record<string, string> = {
  not_started: 'bg-muted text-muted-foreground',
  in_progress: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  done: 'bg-green-500/10 text-green-700 dark:text-green-400',
  skipped: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
};

const SOURCE_LABELS: Record<string, string> = {
  ai: 'the AI agent',
  sdk: 'your SDK',
  manual: 'a teammate',
  crm: 'CRM sync',
};

const StatusBadge = ({
  status,
  source,
  updatedAt,
  interactive,
}: {
  status: string;
  source?: string;
  updatedAt?: string | null;
  interactive?: boolean;
}) => {
  const badge = (
    <span
      className={`inline-flex items-center gap-1 rounded-xl px-2 py-0.5 text-xs font-semibold ${STATUS_TONES[status] || STATUS_TONES.not_started}`}
    >
      {STATUS_LABELS[status] || status}
      {interactive && <ChevronDown className="h-3 w-3" />}
    </span>
  );
  if (!source) return badge;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent>
          Set by {SOURCE_LABELS[source] || source}
          {updatedAt ? ` · ${formatDate(updatedAt)}` : ''}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default function AccountObjectives({ accountId }: { accountId: string }) {
  const { get, patch } = useApi();
  const { stages } = useLifecycleStages();
  const [items, setItems] = useState<AccountObjectiveItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    get(`/accounts/${accountId}/objectives`)
      .then((response) => {
        if (!cancelled) setItems(response.data?.items || []);
      })
      .catch((error) => console.error('Error fetching account objectives:', error))
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const setStatus = async (item: AccountObjectiveItem, status: 'in_progress' | 'done') => {
    try {
      const response = await patch(`/accounts/${accountId}/objectives/${item.id}`, { status });
      setItems((current) =>
        current.map((row) => (row.id === item.id ? { ...row, instance: response.data?.instance } : row))
      );
    } catch (error: any) {
      toast.error(error?.data?.detail || 'Failed to update objective');
    }
  };

  if (!loaded || !items.length) return null;

  return (
    <div className="space-y-2">
      <h2 className="text-xl font-semibold inline-flex items-center gap-2">
        <Target className="h-5 w-5 text-primary" /> Objectives
      </h2>
      <div className="rounded-xl border bg-card divide-y">
        {items.map((item) => {
          const accountStatus = item.instance?.status || 'not_started';
          const people = item.people || [];
          const doneCount = people.filter((p) => p.status === 'done').length;
          const inProgressCount = people.filter((p) => p.status === 'in_progress').length;
          return (
            <div key={item.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">{item.name}</span>
                  <Badge variant="outline" className="shrink-0 text-[10px] uppercase">
                    {item.level}
                  </Badge>
                  {item.stage && (
                    <span className="hidden shrink-0 sm:inline-flex">
                      <StageBadge stageKey={item.stage} stages={stages} />
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{item.description}</p>
              </div>
              {item.level === 'account' ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" className="hover:opacity-80 transition-opacity">
                      <StatusBadge
                        status={accountStatus}
                        source={item.instance?.status_source}
                        updatedAt={item.instance?.status_updated_at}
                        interactive
                      />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(['in_progress', 'done'] as const).map((status) => (
                      <DropdownMenuItem
                        key={status}
                        className="gap-2"
                        onClick={() => status !== accountStatus && setStatus(item, status)}
                      >
                        <StatusBadge status={status} />
                        {status === accountStatus && <Check className="h-3.5 w-3.5 ml-auto" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {people.length
                          ? `${doneCount} done · ${inProgressCount} in progress`
                          : 'No progress yet'}
                      </span>
                    </TooltipTrigger>
                    {!!people.length && (
                      <TooltipContent className="space-y-1">
                        {people.map((person) => (
                          <div key={person.lead_id} className="flex items-center gap-2 text-xs">
                            <span>{person.name || person.email || 'Contact'}</span>
                            <StatusBadge status={person.status} />
                          </div>
                        ))}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
